/**
 * Helpers de visualización horaria Europe/Madrid.
 *
 * Regla backend v0.6:
 * - changedAt → UTC (Z) — cálculos
 * - changedAtLocal → Europe/Madrid sin Z — mostrar / alinear UI
 */

const MADRID_TZ = 'Europe/Madrid';

const WALL_CLOCK_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

export function hasExplicitUtcOrOffset(iso: string): boolean {
  return /Z$/i.test(iso.trim()) || /[+-]\d{2}:?\d{2}$/.test(iso.trim());
}

/** Preferir changedAtLocal para UI; fallback a changedAt */
export function getValveEventDisplayIso(change: {
  changedAt: string;
  changedAtLocal?: string;
}): string {
  return change.changedAtLocal || change.changedAt;
}

/**
 * Interpreta componentes de reloj de pared como ms comparables (Date.UTC),
 * independientes del TZ del navegador. Usar para posicionar timeline.
 */
export function wallClockToComparableMs(iso: string): number {
  const m = iso.trim().match(WALL_CLOCK_RE);
  if (!m) {
    const t = Date.parse(iso);
    return Number.isNaN(t) ? 0 : t;
  }
  const [, y, mo, d, h, mi, s] = m;
  return Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0));
}

/** Convierte un instante UTC a componentes Europe/Madrid y ms comparables */
function utcIsoToMadridComparableMs(iso: string): number {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 0;

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: MADRID_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const hour = get('hour') === '24' ? '00' : get('hour');

  return Date.UTC(
    +get('year'),
    +get('month') - 1,
    +get('day'),
    +hour,
    +get('minute'),
    +get('second')
  );
}

/**
 * Instantánea comparable para barras/marcadores del timeline.
 * - Sin Z/offset → reloj Madrid (changedAtLocal / slots programados)
 * - Con Z → convertir una sola vez a reloj Madrid
 */
export function toTimelineInstant(iso: string): number {
  if (hasExplicitUtcOrOffset(iso)) {
    return utcIsoToMadridComparableMs(iso);
  }
  return wallClockToComparableMs(iso);
}

function formatMadridFromUtc(iso: string, options: Intl.DateTimeFormatOptions): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: MADRID_TZ,
    ...options,
  }).format(date);
}

/** HH:mm en Europe/Madrid (sin doble conversión si ya es wall-clock local) */
export function formatMadridTime(iso: string): string {
  if (!hasExplicitUtcOrOffset(iso)) {
    const m = iso.trim().match(WALL_CLOCK_RE);
    if (m) return `${m[4]}:${m[5]}`;
  }
  return formatMadridFromUtc(iso, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Fecha corta tipo "24 jul" en Europe/Madrid */
export function formatMadridDate(iso: string): string {
  if (!hasExplicitUtcOrOffset(iso)) {
    const m = iso.trim().match(WALL_CLOCK_RE);
    if (m) {
      const months = [
        'ene', 'feb', 'mar', 'abr', 'may', 'jun',
        'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
      ];
      const day = String(+m[3]);
      const month = months[+m[2] - 1] ?? m[2];
      return `${day} ${month}`;
    }
  }
  return formatMadridFromUtc(iso, {
    day: 'numeric',
    month: 'short',
  });
}

/** ISO local sin Z para slots de programación (hora España de pared) */
export function buildLocalWallIso(date: string, hour: number, minute: number, second = 0): string {
  return `${date}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
}
